import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createOrganizationRequest,
  createUserRequest,
  deleteAttachmentRequest,
  fetchAttachments,
  fetchAudit,
  fetchNotifications,
  fetchOrganizations,
  fetchTenantSettings,
  fetchUnreadCount,
  fetchUsers,
  markAllNotificationsRead,
  markNotificationsRead,
  resendInviteRequest,
  updateOrganizationRequest,
  updatePreferencesRequest,
  updateTenantSettingsRequest,
  updateUserRequest,
  uploadAttachmentsRequest,
} from '../api/endpoints';
import type { CommentVisibility, Me, NotificationPrefs, Role } from '../types/api';
import { queryKeys } from './queryKeys';

const keys = {
  notifications: ['notifications'] as const,
  notificationList: (unreadOnly: boolean) => ['notifications', 'list', unreadOnly] as const,
  unread: ['notifications', 'unread'] as const,
  attachments: (ticketId: string) => [...queryKeys.tickets.detail(ticketId), 'attachments'] as const,
  users: (params: object) => ['admin', 'users', params] as const,
  orgs: ['admin', 'organizations'] as const,
  tenant: ['admin', 'tenant'] as const,
  audit: (entityType: string) => ['admin', 'audit', entityType] as const,
};

/* ---------- Notifications ---------- */

export function useUnreadCount(enabled: boolean) {
  // Polling pauses automatically while the tab is hidden (refetchIntervalInBackground defaults to false).
  return useQuery({ queryKey: keys.unread, queryFn: fetchUnreadCount, enabled, refetchInterval: 30_000, staleTime: 15_000 });
}

export function useNotifications(unreadOnly: boolean) {
  return useInfiniteQuery({
    queryKey: keys.notificationList(unreadOnly),
    queryFn: ({ pageParam }) => fetchNotifications(pageParam, unreadOnly),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[] | 'all') => (ids === 'all' ? markAllNotificationsRead() : markNotificationsRead(ids)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.notifications }),
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Partial<NotificationPrefs>) => updatePreferencesRequest(prefs),
    onSuccess: (notificationPrefs) => qc.setQueryData<Me>(queryKeys.me, (prev) => (prev ? { ...prev, notificationPrefs } : prev)),
  });
}

/* ---------- Attachments ---------- */

export function useAttachments(ticketId: string) {
  return useQuery({ queryKey: keys.attachments(ticketId), queryFn: () => fetchAttachments(ticketId) });
}

export function useUploadAttachments(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { files: File[]; visibility: CommentVisibility; onProgress?: (pct: number) => void }) =>
      uploadAttachmentsRequest(ticketId, input.files, input.visibility, input.onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.attachments(ticketId) });
      void qc.invalidateQueries({ queryKey: queryKeys.tickets.timeline(ticketId) });
    },
  });
}

export function useDeleteAttachment(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => deleteAttachmentRequest(ticketId, attachmentId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.attachments(ticketId) }),
  });
}

/* ---------- Administration ---------- */

export function useUsers(params: { q?: string; role?: Role; page: number }) {
  return useQuery({ queryKey: keys.users(params), queryFn: () => fetchUsers(params), placeholderData: keepPreviousData });
}

function useAdminMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>, invalidate: readonly unknown[]) {
  const qc = useQueryClient();
  // Wrapped: TanStack v5 passes a context as a second argument, which must not leak into request functions.
  return useMutation({ mutationFn: (args: TArgs) => fn(args), onSuccess: () => void qc.invalidateQueries({ queryKey: invalidate }) });
}

export const useCreateUser = () => useAdminMutation(createUserRequest, ['admin', 'users']);
export const useUpdateUser = () =>
  useAdminMutation((a: { id: string; body: Parameters<typeof updateUserRequest>[1] }) => updateUserRequest(a.id, a.body), ['admin', 'users']);
export const useResendInvite = () => useMutation({ mutationFn: (id: string) => resendInviteRequest(id) });

export function useOrganizations(enabled = true) {
  return useQuery({ queryKey: keys.orgs, queryFn: fetchOrganizations, enabled, staleTime: 60_000 });
}
export const useCreateOrganization = () => useAdminMutation(createOrganizationRequest, keys.orgs);
export const useUpdateOrganization = () =>
  useAdminMutation((a: { id: string; body: Parameters<typeof updateOrganizationRequest>[1] }) => updateOrganizationRequest(a.id, a.body), keys.orgs);

export function useTenantSettings() {
  return useQuery({ queryKey: keys.tenant, queryFn: fetchTenantSettings });
}
export const useUpdateTenantSettings = () => useAdminMutation(updateTenantSettingsRequest, keys.tenant);

export function useAuditLog(entityType: string) {
  return useInfiniteQuery({
    queryKey: keys.audit(entityType),
    queryFn: ({ pageParam }) => fetchAudit({ ...(entityType ? { entityType } : {}), cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}
